import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, X, ShieldAlert, Cpu, Laptop, Lock, Server, ArrowRight, BatteryCharging, Fingerprint, BrainCircuit } from 'lucide-react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const HardwareComparisonPage = () => {
    const pageRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        let ctx = gsap.context(() => {
            // Hero section reveals — scoped to the hero only
            gsap.utils.toArray('.reveal-up').forEach((el) => {
                gsap.from(el, {
                    y: 40,
                    opacity: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                    }
                });
            });

            // Comparison cards — each card gets its own trigger
            gsap.utils.toArray('.card-reveal').forEach((card, i) => {
                gsap.from(card, {
                    y: 40,
                    opacity: 0,
                    duration: 0.8,
                    delay: i * 0.15,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                    }
                });
            });
        }, pageRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={pageRef} className="pt-32 pb-24 bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4] font-sans">

            {/* Hero / Problem Statement */}
            <section className="max-w-4xl mx-auto px-6 mb-24 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#18181B] border border-[#F0EFF4]/10 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6 reveal-up">
                    <BrainCircuit className="w-4 h-4" />
                    THE NEW FRONTIER
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 reveal-up">
                    "Chatbots in tabs" are dead.<br />
                    <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl md:text-7xl">Welcome your always-on agent.</span>
                </h1>
                <p className="text-lg md:text-xl text-[#F0EFF4]/60 leading-relaxed max-w-2xl mx-auto reveal-up">
                    Transitioning to a local "Chief of Staff" requires more than just a software download; it requires an orchestration layer. OpenClaw isn't the AI—it's the brain-to-system connector. The hardware you choose becomes your agent's armored cockpit.
                </p>
            </section>

            {/* Local vs Cloud Context */}
            <section className="max-w-6xl mx-auto px-6 mb-32">
                <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[3rem] p-8 md:p-16 text-center reveal-up relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-[#7B61FF]/10 to-transparent opacity-50"></div>
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">The "Local-First" Advantage</h2>
                        <p className="text-lg text-[#F0EFF4]/70 max-w-3xl mx-auto leading-relaxed">
                            Deploying on a Virtual Private Server (VPS) exposes you to "noisy neighbors" and requires trusting a data center with your root passwords. Local Apple Silicon gives your agent direct, low-latency access to your memory files while keeping your data physically in your possession.
                        </p>
                    </div>
                </div>
            </section>

            {/* The Comparison */}
            <section className="max-w-6xl mx-auto px-6 mb-32">
                <div className="text-center mb-16 reveal-up">
                    <h2 className="text-3xl md:text-5xl font-bold">Choosing Your Hardware</h2>
                    <p className="mt-4 text-[#F0EFF4]/60">Every OpenClaw deployment includes a pre-configured machine. Here is how they compare.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 comparison-grid">

                    {/* MacBook Air Card */}
                    <div className="bg-gradient-to-br from-[#18181B] to-[#0A0A14] border border-[#7B61FF]/30 rounded-[2rem] p-8 md:p-12 relative overflow-hidden card-reveal hover:-translate-y-2 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_40px_rgba(123,97,255,0.1)] hover:shadow-[0_0_60px_rgba(123,97,255,0.25)] group">
                        <div className="absolute top-0 right-0 p-6 z-10">
                            <div className="px-3 py-1 bg-[#7B61FF]/20 text-[#7B61FF] text-xs font-bold tracking-wider rounded-full border border-[#7B61FF]/30">EXECUTIVE CHOICE</div>
                        </div>
                        <div className="w-full aspect-[4/3] rounded-[1.5rem] overflow-hidden mb-6 -mx-2 relative">
                            <img src="/images/generated/macbook_roofing.png" alt="Obsidian MacBook Air M5 deployed at job site" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>
                        </div>
                        <h3 className="text-3xl font-bold mb-2">M5 MacBook Air (24GB)</h3>
                        <p className="text-[#F0EFF4]/60 mb-8 border-b border-[#F0EFF4]/10 pb-8">The ultimate self-contained survival capsule for your agent.</p>

                        <ul className="space-y-4 mb-8">
                            <li className="flex gap-4">
                                <BatteryCharging className="w-6 h-6 text-[#10B981] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">The Built-in UPS</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">For an orchestrator that must maintain 24/7 persistence, the battery acts as an Uninterruptible Power Supply. If the office loses power, your agent stays online.</span>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <Fingerprint className="w-6 h-6 text-[#10B981] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">Hardware-Level Security</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">Touch ID and the Secure Enclave offer a physical hardware gate for credential management via Bitwarden that a headless server cannot replicate.</span>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <Check className="w-6 h-6 text-[#10B981] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">Bypass the "Magic Mouse Tax"</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">A built-in screen and keyboard ensure you have an immediate interface for "first-mile" troubleshooting before the Tailscale tunnel is active.</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Mac Mini Card */}
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-8 md:p-12 relative opacity-60 card-reveal transition-all duration-500">
                        <div className="w-full aspect-[4/3] rounded-[1.5rem] overflow-hidden mb-6 -mx-2 relative">
                            <img src="/images/generated/mac_mini_roofing.png" alt="Obsidian Mac Mini M4 with roofing blueprints" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>
                        </div>
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-3xl font-bold">M4 Mac Mini</h3>
                            <div className="relative inline-block group/badge">
                                <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold tracking-wider rounded-full border border-amber-500/30 cursor-help">SUPPLY LIMITED</span>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-[#1E1E2E] border border-[#F0EFF4]/20 rounded-xl text-xs text-[#F0EFF4]/80 leading-relaxed shadow-xl opacity-0 group-hover/badge:opacity-100 transition-opacity duration-200 pointer-events-none z-20 text-left font-normal">
                                    Currently sold out. Units can be ordered but expect delivery delays. Contact us to inquire about current availability.
                                </div>
                            </div>
                        </div>
                        <p className="text-[#F0EFF4]/60 mb-8 border-b border-[#F0EFF4]/10 pb-8">The brutalist, always-on orchestration server. We default all deployments to the MacBook Air for its built-in UPS and portability advantages.</p>

                        <ul className="space-y-4 mb-8">
                            <li className="flex gap-4">
                                <Check className="w-6 h-6 text-[#10B981] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">Thermal Sustenance</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">Active cooling allows the system to process massive local vector embeddings and compile code without hitting a thermal throttling wall.</span>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <Check className="w-6 h-6 text-[#10B981] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">Maximum Compute Density</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">You aren't paying for a screen or battery. It is pure inference power, ideal for running high-speed quantized models like DeepSeek locally.</span>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <X className="w-6 h-6 text-[#E63B2E] flex-shrink-0" />
                                <div>
                                    <strong className="block text-[#F0EFF4]">The Peripheral Bottleneck</strong>
                                    <span className="text-[#F0EFF4]/60 text-sm">Requires external monitors and keyboards for initial setup, often leading to immediate Bluetooth pairing frustrations out of the box.</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Security Deep Dive */}
            <section className="max-w-4xl mx-auto px-6 mb-32">
                <h3 className="text-2xl font-bold mb-8 text-center reveal-up">The Security "Sandwich"</h3>
                <div className="space-y-6">
                    <div className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-2xl p-6 md:p-8 flex gap-6 items-start reveal-up">
                        <ShieldAlert className="w-8 h-8 text-[#E63B2E] flex-shrink-0 mt-1" />
                        <div>
                            <h4 className="text-xl font-bold mb-2">The "Dirty Data" Protocol</h4>
                            <p className="text-[#F0EFF4]/60 leading-relaxed">Connecting an AI to a primary Gmail is a security nightmare. A malicious email can trigger a prompt injection. We utilize "Infrastructure Gmails" and mandate that only verified senders forward data to the agent.</p>
                        </div>
                    </div>
                    <div className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-2xl p-6 md:p-8 flex gap-6 items-start reveal-up">
                        <Server className="w-8 h-8 text-[#7B61FF] flex-shrink-0 mt-1" />
                        <div>
                            <h4 className="text-xl font-bold mb-2">Tailscale Loopback Binding</h4>
                            <p className="text-[#F0EFF4]/60 leading-relaxed">We do not expose public ports. OpenClaw routes through a Tailscale mesh using loopback binding (\`tailscale up --ssh\`). Only your authenticated devices can communicate with the gateway.</p>
                        </div>
                    </div>
                    <div className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-2xl p-6 md:p-8 flex gap-6 items-start reveal-up">
                        <BrainCircuit className="w-8 h-8 text-[#7B61FF] flex-shrink-0 mt-1" />
                        <div>
                            <h4 className="text-xl font-bold mb-2">The "Bicameral Mind" Architecture</h4>
                            <p className="text-[#F0EFF4]/60 leading-relaxed">We decouple "Why" from "How." A Steward agent handles strategy and communication using a dedicated \`SOUL.md\`, while a separate Builder agent handles execution. They communicate via a strict JSON Synapse payload to eliminate hallucinatory errors.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="text-center reveal-up pb-12">
                <h2 className="text-3xl font-bold mb-6">The Ultimate Economic Arbitrage</h2>
                <p className="text-lg text-[#F0EFF4]/60 max-w-2xl mx-auto mb-8">
                    Replacing a human executive assistant costing $5,000/month with a $200/month high-tier AI model on local hardware is the ultimate productivity hack.
                </p>
                <Link to="/pricing" className="inline-flex items-center gap-2 bg-[#7B61FF] text-[#F0EFF4] px-8 py-4 rounded-full font-bold hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]">
                    See Pricing
                    <ArrowRight className="w-5 h-5" />
                </Link>
            </section>
        </div>
    );
};

export default HardwareComparisonPage;
